import { type ReactNode, useRef, useState } from "react";

import { CommandDialog } from "../../ui/command-dialog";
import { TextAreaField } from "../../ui/fields";
import { describeDiagnosticsError } from "../errors";

// The shell of every recovery dialog of the diagnostics area (CHT-772): re-run a job, pause or resume a task family, reconcile a payment,
// resolve a payment exception. Each action is a supported command of the API, and each goes through this one dialog, which is a thin
// adapter on the generic `CommandDialog` of the kit (`src/ui/command-dialog`). The rules come from the kit:
//   - a CONFIRMATION: nothing is sent until the person says what will happen is what they want;
//   - ONE Idempotency-Key for the dialog: a retry after a failure that may have run sends the SAME key and the SAME body, so the server runs
//     the action once. While a retry is possible the fields are locked, so the body cannot change under the key;
//   - Escape and Cancel never close the dialog while the action runs (a closed dialog could not receive its own answer);
//   - a FAILURE stays visible in the dialog and never reads as success. An answer that says "the provider could not answer" is a failure too
//     (`ok: false`): the action did not do its job, so the incident is NOT resolved. The kit keeps the dialog open (`notDone`), the page
//     behind reads again, and the next try ("Try once more") is another action, so it gets another key.

const REASON_MAX = 500;

/** What a send says about itself. `ok: false` is a result that is NOT a success (for example a reconcile whose provider did not answer). */
type RecoveryResult = Readonly<{ ok: boolean; message: string }>;

type RecoveryBuild =
  | Readonly<{
      ok: true;
      /** `reason` is the checked staff reason when the dialog asks for one (`reason="text"`), else an empty string. */
      send: (reason: string, key: string) => Promise<RecoveryResult>;
    }>
  /** A field is invalid: the dialog has set its own error and the first invalid control takes focus. */
  | Readonly<{ ok: false }>;

type Props = Readonly<{
  title: string;
  /** What the action does and what it does not do, in plain words. */
  description: ReactNode;
  confirmLabel: string;
  tone?: "primary" | "danger";
  /** `text`: the dialog asks for a staff reason (1 to 500 characters). `none`: the action takes no free-text reason. */
  reason?: "text" | "none";
  reasonLabel?: string;
  /** The extra fields of this action (a reason code, a resolution code). */
  children?: ReactNode;
  build: () => RecoveryBuild;
  /** The action worked: the page reads again, says so and closes the dialog. */
  onDone: (message: string) => void;
  /** The page is out of date or the action was refused: the page behind reads again (the dialog stays open to say why). */
  onReload: () => void;
  onClose: () => void;
}>;

export function RecoveryDialog({
  title,
  description,
  confirmLabel,
  tone = "primary",
  reason: reasonMode = "none",
  reasonLabel = "Reason",
  children,
  build,
  onDone,
  onReload,
  onClose,
}: Props) {
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | undefined>();
  // The action built by the last successful check, and the reason it was checked with; `mutate` sends them.
  const action = useRef<Extract<RecoveryBuild, { ok: true }> | null>(null);
  const checkedReason = useRef("");

  function validate(): boolean {
    const trimmed = reason.trim();
    const missing =
      reasonMode === "none"
        ? undefined
        : trimmed === ""
          ? "Write a reason."
          : trimmed.length > REASON_MAX
            ? `Use at most ${REASON_MAX} characters.`
            : undefined;
    setReasonError(missing);
    // `build()` runs even when the reason is missing, so every invalid field shows its error at once.
    const built = build();
    if (missing !== undefined || !built.ok) return false;
    action.current = built;
    checkedReason.current = trimmed;
    return true;
  }

  return (
    <CommandDialog<string>
      title={title}
      description={description}
      confirmLabel={confirmLabel}
      notDoneRetryLabel="Try once more"
      tone={tone}
      validate={validate}
      mutate={async (key) => {
        try {
          const result = await action.current?.send(checkedReason.current, key);
          if (result === undefined) {
            return {
              ok: false,
              failure: { message: "Something went wrong. Try again.", retryable: false },
            };
          }
          // The action ran and did not do its job: it stays on screen, the incident is not resolved, and the next try is a NEW action.
          return result.ok
            ? { ok: true, value: result.message }
            : { ok: false, notDone: { message: result.message } };
        } catch (error) {
          const described = describeDiagnosticsError(error);
          return {
            ok: false,
            failure: {
              message: described.message,
              retryable: described.retryable,
              // An invalid answer lets the person fix a field; the next body is another action, so the kit gives it another key.
              keepForm: described.kind === "invalid",
              reload: described.reload,
              tone: described.kind === "conflict" ? "conflict" : "error",
              kind: described.kind,
            },
          };
        }
      }}
      onReload={onReload}
      onFailureReload={onReload}
      onDone={onDone}
      onClose={onClose}
    >
      {children}
      {reasonMode === "text" ? (
        <TextAreaField
          id="diagnostics-reason"
          label={reasonLabel}
          hint="Staff only. It goes into the audit trail. The customer never sees it."
          value={reason}
          rows={3}
          maxLength={REASON_MAX}
          required
          error={reasonError}
          onChange={setReason}
        />
      ) : null}
    </CommandDialog>
  );
}
